import { ExecutionContext, ParseNode, GlobalContext, ExecuteResult, ExpressionNode, ConditionNode } from '../types';
import { PromptParser } from './PromptParser';
import { ExpressionExecutor } from './ExpressionExecutor';
import { promptAllFunctions } from '../functions';
import { renderExecuteResult } from '../formatter';

// 保留：已通过传入完整元数据进行校验，不再单独使用名称集合

/**
 * 提示词模板渲染器
 * 负责将解析后的语法树渲染为最终文本
 */
export class PromptRenderer {
  private parser: PromptParser;
  private executor: ExpressionExecutor;

  constructor(context: ExecutionContext) {
    // 传入完整的函数元数据，便于在解析期做参数校验
    this.parser = new PromptParser(promptAllFunctions);
    this.executor = new ExpressionExecutor(context);

    // 初始化递归渲染相关属性
    if (!context.includeStack) {
      context.includeStack = [];
    }
    if (!context.maxIncludeDepth) {
      context.maxIncludeDepth = 10;
    }
    if (!context.includeCache) {
      context.includeCache = new Map();
    }

    // 初始化本地内存存储
    if (!context.localMemory) {
      context.localMemory = new Map();
    }

    // 设置递归渲染方法
    context.renderInclude = this.renderInclude.bind(this);
  }

  /**
   * 对外提供的静态方法：渲染模板（只需要传入上下文和函数）
   * @param template 模板字符串
   * @param globalContext 全局上下文
   * @param customFunctions 自定义函数（可选）
   * @returns 渲染后的文本
   */
  static renderTemplate(
    template: string,
    globalContext: GlobalContext,
  ): string {
    const context: ExecutionContext = {
      variables: {},
      functions: promptAllFunctions,
      globalContext,
      localMemory: new Map()
    };

    const renderer = new PromptRenderer(context);
    const result = renderer.render(template);
    // 去除前后空行
    return result.trim();
  }

  /**
   * 对外提供的静态方法：检查模板语法是否正确
   * @param template 模板字符串
   * @param knownFunctions 可选的已知函数名数组，用于在解析期校验未知函数
   * @returns 语法检查结果，包含是否有效和错误信息
   */
  static checkSyntax(template: string): { isValid: boolean; error?: unknown } {
    try {
      const parser = new PromptParser(promptAllFunctions);
      parser.parse(template);
      return { isValid: true };
    } catch (error: any) {
      return {
        isValid: false,
        error: error,
      };
    }
  }

  /**
   * 渲染模板
   * @param template 模板字符串
   * @returns 渲染后的文本
   */
  render(template: string): string {
    const nodes = this.parser.parse(template);
    return this.renderNodes(nodes);
  }

  parse(template: string) {
    return this.parser.parse(template)
  }

  /**
   * 渲染节点数组
   * @param nodes 节点数组
   * @returns 渲染后的文本
   */
  private renderNodes(nodes: ParseNode[]): string {
    return nodes.map(node => this.renderNode(node)).join('');
  }

  /**
   * 渲染单个节点
   * @param node 要渲染的节点
   * @returns 渲染后的文本
   */
  private renderNode(node: ParseNode): string {
    switch (node.type) {
      case 'text':
        return node.content;
      case 'expression':
        if (node.ast?.type === 'functionCall' && node.ast.name === 'include') {
          const args = node.ast.args.map(arg => arg.type === 'literal' ? arg.value : arg.name);
          return this.renderInclude(args[0], this.executor.context);
        }
        return this.renderExpression(node);
      case 'condition':
        return this.renderCondition(node);
      default:
        return '';
    }
  }

  /**
   * 渲染表达式节点
   * @param node 表达式节点
   * @returns 渲染后的文本
   */
  private renderExpression(node: ExpressionNode): string {
    let result: ExecuteResult = this.executor.executeAST(node.ast);
    const con = renderExecuteResult(this.executor.context, [result])?.join(', ') || '';
    return con;
  }

  /**
   * 渲染条件节点
   * @param node 条件节点
   * @returns 渲染后的文本
   */
  private renderCondition(node: ConditionNode): string {
    const result = this.executor.executeAST(node.conditionAST);
    if (result) {
      return this.renderNodes(node.children);
    } else {
      return this.renderNodes(node.elseChildren || []);
    }
  }

  /**
   * 递归渲染 include 模板
   * @param includePath include 路径
   * @param context 执行上下文
   * @returns 渲染后的文本
   */
  public renderInclude(includePath: string, context: ExecutionContext): string {
    // 检查循环引用
    if (context.includeStack!.includes(includePath)) {
      const cycle = [...context.includeStack!, includePath].join(' -> ');
      throw new Error(`Circular include detected: ${cycle}`);
    }

    // 检查递归深度
    if (context.includeStack!.length >= context.maxIncludeDepth!) {
      throw new Error(`Maximum include depth (${context.maxIncludeDepth}) exceeded`);
    }

    // 检查缓存
    if (context.includeCache!.has(includePath)) {
      return context.includeCache!.get(includePath)!;
    }
    const { getPromptComponent } = context.globalContext.generateStore.getState();
    const prompt = getPromptComponent(includePath);
    // 获取模板内容
    const template = prompt?.attributes.messages.map(message => message.text).join('') || '';
    if (template === '' || !prompt) {
      return ''
    }
    const globalContext: ExecutionContext['globalContext'] = {
      ...context.globalContext,
      currentPosition: {
        ...context.globalContext.currentPosition,
        prompt: prompt,
      },
    };
    // 创建新的渲染器实例进行递归渲染
    const newContext: ExecutionContext = {
      ...context,
      globalContext: globalContext,
      includeStack: [...context.includeStack!, includePath],
    };

    const newRenderer = new PromptRenderer(newContext);
    const result = newRenderer.render(template);

    // 缓存结果
    context.includeCache!.set(includePath, result);

    return result;
  }

  // 将解析/渲染错误格式化为多行信息（带行列和指示符）
  static formatErrorForDisplay(error: unknown, sourceText: string): string {
    const err: any = error;
    const isParseError = err && typeof err === 'object' && err.name === 'ParseError' && typeof err.line === 'number' && typeof err.column === 'number';
    const baseMsg = err instanceof Error ? err.message.replace(/^Error:\s*/, '') : String(err);

    if (!isParseError) {
      return `Error: ${baseMsg}`;
    }

    const lineNum: number = err.line;
    const colNum: number = err.column;
    const lines = sourceText.split('\n');
    const lineText = lines[lineNum - 1] ?? '';
    const prefix = `${lineNum}${' '.repeat(Math.min(3 - lineNum.toString().length))}| `;
    const arrowLine = `<span style="opacity: 0;">${' '.repeat(Math.max(colNum - 1, 0))}</span>^`;
    return `Error: ${baseMsg}\n --> template:${lineNum}:${colNum}\n   |\n${prefix}${lineText}\n   | ${arrowLine}`;
  }
} 